import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { BrowserRouter, Routes, Route } from "react-router-dom";

import Index from "./pages/Index";
import Login from "./pages/Login";
import Devices from "./pages/Devices";
import Users from "./pages/Users";
import Trips from "./pages/Trips";
import Stops from "./pages/Stops";
import Summary from "./pages/Summary";
import Reports from "./pages/Reports";
import Cameras from "./pages/Cameras";
import Geofences from "./pages/Geofences";
import Notifications from "./pages/Notifications";
import Settings from "./pages/Settings";
import Customers from "./pages/Customers";
import SimCards from "./pages/SimCards";
import Contracts from "./pages/Contracts";
import Invoices from "./pages/Invoices";
import FieldServices from "./pages/FieldServices";
import Maintenance from "./pages/Maintenance";
import PlannedRoutes from "./pages/PlannedRoutes";
import PlannedRouteEditor from "./pages/PlannedRouteEditor";
import Commands from "./pages/Commands";
import DeviceStock from "./pages/DeviceStock";
import ResellerDashboard from "./pages/ResellerDashboard";
import Drivers from "./pages/Drivers";
import FullMap from "./pages/FullMap";
import PerformanceReport from "./pages/PerformanceReport";
import LiveMap from "./pages/LiveMap";
import NotFound from "./pages/NotFound";

if (import.meta.env.MODE === "production") {
  void import("@sentry/react").then((Sentry) => {
    Sentry.init({
      dsn: "https://a407f6e563ed11632897ab5bc873af10@o4511099901902848.ingest.de.sentry.io/4511099903541328",
      sendDefaultPii: true,
    });
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
    },
  },
});

const persister = createAsyncStoragePersister({
  storage: window.localStorage,
  key: 'REACT_QUERY_OFFLINE_CACHE_v2',
});

function applyTheme() {
  if (navigator.userAgent.includes('com.fleetmap.jsrastreamento')) {
    document.documentElement.classList.remove("dark");
    return;
  }
  const saved = localStorage.getItem("app_settings");
  const theme = saved ? JSON.parse(saved).theme : "system";
  if (theme === "dark") {
    document.documentElement.classList.add("dark");
  } else if (theme === "light") {
    document.documentElement.classList.remove("dark");
  } else {
    // system
    if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }
}

const App = () => {
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => applyTheme();
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return (
  <PersistQueryClientProvider client={queryClient} persistOptions={{
    persister,
    buster: 'success-only-cache-v1',
    dehydrateOptions: {
      shouldDehydrateQuery: (query) => query.queryKey[0] !== 'session' && query.state.status === 'success',
    },
  }}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/devices" element={<Devices />} />
          <Route path="/cameras" element={<Cameras />} />
          <Route path="/users" element={<Users />} />
          <Route path="/history/trips" element={<Trips />} />
          <Route path="/history/stops" element={<Stops />} />
          <Route path="/history/summary" element={<Summary />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/geofences" element={<Geofences />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/drivers" element={<Drivers />} />
          <Route path="/customers" element={<Customers />} />
          <Route path="/sim-cards" element={<SimCards />} />
          <Route path="/contracts" element={<Contracts />} />
          <Route path="/invoices" element={<Invoices />} />
          <Route path="/field-services" element={<FieldServices />} />
          <Route path="/maintenance" element={<Maintenance />} />
          <Route path="/routes" element={<PlannedRoutes />} />
          <Route path="/routes/:id" element={<PlannedRouteEditor />} />
          <Route path="/commands" element={<Commands />} />
          <Route path="/device-stock" element={<DeviceStock />} />
          <Route path="/reseller" element={<ResellerDashboard />} />
          <Route path="/performance-report" element={<PerformanceReport />} />
          <Route path="/live-map" element={<LiveMap />} />
          <Route path="/map" element={<FullMap />} />
          <Route path="/login" element={<Login />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </PersistQueryClientProvider>
  );
};

export default App;
